import React, { useState } from 'react';
import { 
  Search, 
  Filter, 
  X,
  ChevronDown,
  ChevronUp,
  Star,
  Users,
  Award,
  MapPin,
  Calendar,
  DollarSign
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '../../ui/avatar';
import axios from 'axios';

const AdvancedTrainerSearch = () => {
  const [showFilters, setShowFilters] = useState(true);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    states: [],
    locations: [],
    skills: '',
    others: '',
    gender: '',
    virtual: false,
    languages: '',
    professionalFeeMin: 0,
    professionalFeeMax: 200000,
    profilePercentageMin: 0,
    profilePercentageMax: 100,
    industry: '',
    registeredDateFrom: '',
    registeredDateTo: '',
    trainerName: ''
  });

  // Data for dropdowns
  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
    'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
    'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan',
    'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
    'Uttarakhand', 'West Bengal'
  ];

  const gujaratCities = [
    'Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Bhavnagar', 'Jamnagar',
    'Junagadh', 'Gandhinagar', 'Anand', 'Nadiad', 'Morbi', 'Surendranagar',
    'Bharuch', 'Mehsana', 'Bhuj', 'Porbandar', 'Palanpur', 'Vapi', 'Dwarka',
    'Navsari', 'Valsad', 'Godhra'
  ];

  // Handle state selection (multi-select with tags)
  const handleStateToggle = (state) => {
    setFilters(prev => ({
      ...prev,
      states: prev.states.includes(state)
        ? prev.states.filter(s => s !== state)
        : [...prev.states, state]
    }));
  };

  // Handle location selection (multi-select with tags)
  const handleLocationToggle = (location) => {
    setFilters(prev => ({
      ...prev,
      locations: prev.locations.includes(location)
        ? prev.locations.filter(l => l !== location)
        : [...prev.locations, location]
    }));
  };

  // Remove tag
  const removeState = (state) => {
    setFilters(prev => ({
      ...prev,
      states: prev.states.filter(s => s !== state)
    }));
  };

  const removeLocation = (location) => {
    setFilters(prev => ({
      ...prev,
      locations: prev.locations.filter(l => l !== location)
    }));
  };

  // Handle search
  const handleSearch = async () => {
    setIsSearching(true);
    try {
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      // Prepare search params
      const searchParams = {
        states: filters.states,
        locations: filters.locations,
        skills: filters.skills,
        others: filters.others,
        gender: filters.gender,
        virtual: filters.virtual,
        languages: filters.languages,
        fee_min: filters.professionalFeeMin,
        fee_max: filters.professionalFeeMax,
        profile_percentage_min: filters.profilePercentageMin,
        profile_percentage_max: filters.profilePercentageMax,
        industry: filters.industry,
        registered_from: filters.registeredDateFrom,
        registered_to: filters.registeredDateTo,
        trainer_name: filters.trainerName
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/trainers/advanced-search`,
        searchParams,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSearchResults(response.data.trainers || []);
    } catch (error) {
      console.error('Search error:', error);
      
      // For demo purposes, show mock results
      setSearchResults([
        {
          id: 1,
          name: 'Dr. Sarah Johnson',
          email: 'sarah.johnson@example.com',
          specialization: 'Leadership Development',
          location: 'Ahmedabad, Gujarat',
          rating: 4.8,
          experience: '12 years',
          fee: 15000,
          profile_completion: 95,
          skills: ['Leadership', 'Team Building', 'Strategic Planning'],
          virtual_available: true
        },
        {
          id: 2,
          name: 'Rajesh Kumar',
          email: 'rajesh.kumar@example.com',
          specialization: 'Technical Training',
          location: 'Surat, Gujarat',
          rating: 4.6,
          experience: '8 years',
          fee: 12000,
          profile_completion: 88,
          skills: ['Software Development', 'Agile', 'DevOps'],
          virtual_available: true
        }
      ]);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle reset
  const handleReset = () => {
    setFilters({
      states: [],
      locations: [],
      skills: '',
      others: '',
      gender: '',
      virtual: false,
      languages: '',
      professionalFeeMin: 0,
      professionalFeeMax: 200000,
      profilePercentageMin: 0,
      profilePercentageMax: 100,
      industry: '',
      registeredDateFrom: '',
      registeredDateTo: '',
      trainerName: ''
    });
    setSearchResults([]);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Search className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Advanced Trainer Search</h1>
            <p className="text-gray-600">Find the perfect trainer for your organization</p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center space-x-2"
        >
          <Filter className="w-4 h-4" />
          <span>{showFilters ? 'Hide' : 'Show'} Filters</span>
          {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </Button>
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Search Filters</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* States */}
            <div>
              <Label className="text-base font-semibold mb-2 block">States</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {filters.states.map(state => (
                  <Badge key={state} variant="secondary" className="flex items-center space-x-1">
                    <span>{state}</span>
                    <X
                      className="w-3 h-3 cursor-pointer hover:text-red-600"
                      onClick={() => removeState(state)}
                    />
                  </Badge>
                ))}
              </div>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                value=""
                onChange={(e) => e.target.value && handleStateToggle(e.target.value)}
              >
                <option value="">Select a state to add...</option>
                {indianStates.map(state => (
                  <option key={state} value={state} disabled={filters.states.includes(state)}>
                    {state}
                  </option>
                ))}
              </select>
            </div>

            {/* Location/City */}
            <div>
              <Label className="text-base font-semibold mb-2 block">Location/City</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {filters.locations.map(location => (
                  <Badge key={location} variant="secondary" className="flex items-center space-x-1">
                    <span>{location}</span>
                    <X
                      className="w-3 h-3 cursor-pointer hover:text-red-600"
                      onClick={() => removeLocation(location)}
                    />
                  </Badge>
                ))}
              </div>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                value=""
                onChange={(e) => e.target.value && handleLocationToggle(e.target.value)}
              >
                <option value="">Select a city to add...</option>
                {gujaratCities.map(city => (
                  <option key={city} value={city} disabled={filters.locations.includes(city)}>
                    {city}
                  </option>
                ))}
              </select>
            </div>

            {/* Skills */}
            <div>
              <Label htmlFor="skills" className="text-base font-semibold">Skills</Label>
              <Input
                id="skills"
                placeholder="Skills ..."
                value={filters.skills}
                onChange={(e) => setFilters({ ...filters, skills: e.target.value })}
                className="mt-2"
              />
            </div>

            {/* Others (Designation/Experience) */}
            <div>
              <Label htmlFor="others" className="text-base font-semibold">Others</Label>
              <Input
                id="others"
                placeholder="designation/experience..."
                value={filters.others}
                onChange={(e) => setFilters({ ...filters, others: e.target.value })}
                className="mt-2"
              />
            </div>

            {/* Gender and Virtual */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-base font-semibold mb-2 block">Gender</Label>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="gender"
                      value="Male"
                      checked={filters.gender === 'Male'}
                      onChange={(e) => setFilters({ ...filters, gender: e.target.value })}
                      className="mr-2"
                    />
                    Male
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="gender"
                      value="Female"
                      checked={filters.gender === 'Female'}
                      onChange={(e) => setFilters({ ...filters, gender: e.target.value })}
                      className="mr-2"
                    />
                    Female
                  </label>
                </div>
              </div>

              <div>
                <Label className="text-base font-semibold mb-2 block">Virtual</Label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.virtual}
                    onChange={(e) => setFilters({ ...filters, virtual: e.target.checked })}
                    className="mr-2 w-4 h-4"
                  />
                  Virtual
                </label>
              </div>
            </div>

            {/* Languages */}
            <div>
              <Label htmlFor="languages" className="text-base font-semibold">Languages</Label>
              <Input
                id="languages"
                placeholder="Languages ..."
                value={filters.languages}
                onChange={(e) => setFilters({ ...filters, languages: e.target.value })}
                className="mt-2"
              />
            </div>

            {/* Professional Fee Range */}
            <div>
              <Label className="text-base font-semibold mb-2 block">Professional Fee</Label>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{filters.professionalFeeMin}</span>
                  <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                    {filters.professionalFeeMax.toLocaleString()}
                  </Badge>
                </div>
                <input
                  type="range"
                  min="0"
                  max="200000"
                  step="1000"
                  value={filters.professionalFeeMax}
                  onChange={(e) => setFilters({ ...filters, professionalFeeMax: parseInt(e.target.value) })}
                  className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                />
              </div>
            </div>

            {/* Profile Percentage Range */}
            <div>
              <Label className="text-base font-semibold mb-2 block">
                <a href="#" className="text-blue-600 hover:underline">Profile Percentage</a>
              </Label>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{filters.profilePercentageMin}</span>
                  <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                    {filters.profilePercentageMax}
                  </Badge>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={filters.profilePercentageMax}
                  onChange={(e) => setFilters({ ...filters, profilePercentageMax: parseInt(e.target.value) })}
                  className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                />
              </div>
            </div>

            {/* Industry */}
            <div>
              <Label htmlFor="industry" className="text-base font-semibold">Industry</Label>
              <Input
                id="industry"
                placeholder="Industry ..."
                value={filters.industry}
                onChange={(e) => setFilters({ ...filters, industry: e.target.value })}
                className="mt-2"
              />
            </div>

            {/* Registered Dates */}
            <div>
              <Label className="text-base font-semibold mb-2 block">Registered Dates</Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="dateFrom" className="text-sm">From</Label>
                  <Input
                    id="dateFrom"
                    type="date"
                    value={filters.registeredDateFrom}
                    onChange={(e) => setFilters({ ...filters, registeredDateFrom: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="dateTo" className="text-sm">To</Label>
                  <Input
                    id="dateTo"
                    type="date"
                    value={filters.registeredDateTo}
                    onChange={(e) => setFilters({ ...filters, registeredDateTo: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* T-entry (Name search) */}
            <div>
              <Label htmlFor="trainerName" className="text-base font-semibold">T-entry:</Label>
              <Input
                id="trainerName"
                placeholder="Name ..."
                value={filters.trainerName}
                onChange={(e) => setFilters({ ...filters, trainerName: e.target.value })}
                className="mt-2"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={handleReset}
                className="bg-red-50 text-red-600 hover:bg-red-100 border-red-200"
              >
                Reset
              </Button>
              <Button
                onClick={handleSearch}
                disabled={isSearching}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Search className="w-4 h-4 mr-2" />
                {isSearching ? 'Searching...' : 'Search'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">
              Search Results ({searchResults.length})
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {searchResults.map((trainer) => (
              <Card key={trainer.id} className="hover:shadow-lg transition-shadow duration-200">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4 mb-4">
                    <Avatar className="w-16 h-16">
                      <AvatarImage src={trainer.avatar} alt={trainer.name} />
                      <AvatarFallback className="bg-blue-600 text-white font-semibold text-lg">
                        {trainer.name?.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-gray-900">{trainer.name}</h3>
                      <p className="text-blue-600 font-medium">{trainer.specialization}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <div className="flex items-center">
                          <Star className="w-4 h-4 text-yellow-500 mr-1" />
                          <span className="font-medium">{trainer.rating}</span>
                        </div>
                        <span className="text-gray-400">•</span>
                        <span className="text-gray-600 text-sm">{trainer.experience}</span>
                      </div>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="w-4 h-4 mr-2" />
                      {trainer.location}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <DollarSign className="w-4 h-4 mr-2" />
                      Professional Fee: ₹{trainer.fee?.toLocaleString()} per day
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Users className="w-4 h-4 mr-2" />
                      Profile Completion: {trainer.profile_completion}%
                    </div>
                  </div>

                  {/* Skills */}
                  {trainer.skills && trainer.skills.length > 0 && (
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-1">
                        {trainer.skills.slice(0, 3).map((skill, index) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {skill}
                          </Badge>
                        ))}
                        {trainer.skills.length > 3 && (
                          <Badge variant="secondary" className="text-xs">
                            +{trainer.skills.length - 3} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Virtual Badge */}
                  {trainer.virtual_available && (
                    <Badge className="bg-green-100 text-green-800 mb-4">
                      Virtual Available
                    </Badge>
                  )}

                  {/* Actions */}
                  <div className="flex space-x-2 pt-3 border-t">
                    <Button size="sm" variant="outline" className="flex-1">
                      View Profile
                    </Button>
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700 flex-1">
                      Contact Trainer
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isSearching && searchResults.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No results yet</h3>
            <p className="text-gray-500">
              Use the filters above to search for trainers that match your requirements
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdvancedTrainerSearch;
